import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { PushNotificationToggle } from '../../components/PushNotificationToggle';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Save, Clock, Image, MapPin, Upload, X, Link as LinkIcon, CreditCard, AlertTriangle, Megaphone, Bell, Calendar, CalendarX, Trash2, Wallet, Percent, Euro, Info, FileText, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl } from '../../utils/images';
import { getApiErrorMessage } from '../../utils/errorHelper';

const cuisineOptions = [
  'Burger', 'Pizza', 'Sushi', 'Japonais', 'Salade bar', 'Healthy',
  'Américain', 'Français', 'Italien', 'Asiatique', 'Mexicain', 'Indien'
];

// Options de modes de paiement acceptés au comptoir
const paymentMethodOptions = [
  { id: 'cash', label: 'Espèces', icon: '💵' },
  { id: 'card', label: 'Carte bancaire', icon: '💳' },
  { id: 'ticket_restaurant_card', label: 'Carte tickets restaurants', icon: '🎫' },
  { id: 'ticket_restaurant_paper', label: 'Tickets restaurants papier', icon: '🎟️' },
  { id: 'american_express', label: 'American Express', icon: '💎' },
  { id: 'bitcoin', label: 'Bitcoin', icon: '₿' },
];

// Options pour le délai maximum de réservation
const maxDaysOptions = [
  { value: 6, label: '6 heures' },
  { value: 12, label: '12 heures' },
  { value: 24, label: '1 jour' },
  { value: 48, label: '2 jours' },
  { value: 72, label: '3 jours' },
  { value: 96, label: '4 jours' },
  { value: 168, label: '1 semaine' },
];

export default function RestaurantSettings() {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  
  // Reservations state
  const [reservationsEnabled, setReservationsEnabled] = useState(false);
  const [reservationsMaxDays, setReservationsMaxDays] = useState(48);
  const [reservationsBlockedDates, setReservationsBlockedDates] = useState([]);
  const [reservationsCancellationPolicy, setReservationsCancellationPolicy] = useState('');
  const [savingReservations, setSavingReservations] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docCategories, setDocCategories] = useState([]);
  // Stock alerts toggle
  const [showStockAlerts, setShowStockAlerts] = useState(true);
  // Deposit (Acompte) state
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [depositDefaultType, setDepositDefaultType] = useState('percentage'); // 'percentage' or 'fixed'
  const [depositDefaultValue, setDepositDefaultValue] = useState(30);
  const [depositApplyToAll, setDepositApplyToAll] = useState(false);
  const [depositMinOrderAmount, setDepositMinOrderAmount] = useState('');
  const [depositRefundPolicy, setDepositRefundPolicy] = useState('');
  const [depositRefundHours, setDepositRefundHours] = useState(48);
  const [savingDeposit, setSavingDeposit] = useState(false);
  
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [settings, setSettings] = useState({
    name: '',
    address: '',
    city: '',
    zipcode: '',
    phone: '',
    email: '',
    description: '',
    cuisine_type: [],
    logo: '',
    cover_image: '',
    avg_prep_time: 20,
    is_active: true,
    accepted_payment_methods: [],
    iban: '',
    bic: '',
    vat_number: ''
  });
  const [announcement, setAnnouncement] = useState({
    text: '',
    active: false,
    type: 'info'  // 'promo', 'info', 'alert'
  });

  const fetchSettings = useCallback(async () => {
    try {
      const [settingsRes, announcementRes, reservationsRes, depositRes] = await Promise.all([
        axios.get(`${API}/restaurant-dashboard/settings`),
        axios.get(`${API}/restaurant-dashboard/announcement`),
        axios.get(`${API}/restaurant-dashboard/reservations/settings`).catch(() => ({ data: {} })),  // Fallback si l'endpoint n'existe pas encore
        axios.get(`${API}/restaurant-dashboard/deposit/settings`).catch(() => ({ data: {} }))  // Fallback si l'endpoint n'existe pas encore
      ]);
      
      setSettings({
        ...settingsRes.data,
        // S'assurer que accepted_payment_methods est toujours un tableau
        accepted_payment_methods: settingsRes.data.accepted_payment_methods || []
      });
      
      // Alertes stock
      setShowStockAlerts(settingsRes.data.show_stock_alerts !== false);
      
      setAnnouncement({
        text: announcementRes.data.announcement || '',
        active: announcementRes.data.announcement_active || false,
        type: announcementRes.data.announcement_type || 'info'
      });
      
      // Paramètres de réservation
      if (reservationsRes.data) {
        setReservationsEnabled(reservationsRes.data.reservations_enabled || false);
        setReservationsMaxDays(reservationsRes.data.reservations_max_hours || reservationsRes.data.reservations_max_days * 24 || 48);
        setReservationsBlockedDates(reservationsRes.data.reservations_blocked_dates || []);
        setReservationsCancellationPolicy(reservationsRes.data.reservations_cancellation_policy || '');
      }
      
      // Paramètres d'acompte
      if (depositRes.data) {
        setDepositEnabled(depositRes.data.deposit_enabled || false);
        setDepositDefaultType(depositRes.data.deposit_default_type || 'percentage');
        setDepositDefaultValue(depositRes.data.deposit_default_value || 30);
        setDepositApplyToAll(depositRes.data.deposit_apply_to_all || false);
        setDepositMinOrderAmount(depositRes.data.deposit_min_order_amount || '');
        setDepositRefundPolicy(depositRes.data.deposit_refund_policy || '');
        setDepositRefundHours(depositRes.data.deposit_refund_hours || 48);
      }
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    // Fetch documents
    const fetchDocs = async () => {
      try {
        const [docsRes, catsRes] = await Promise.all([
          axios.get(`${API}/documents/my`),
          axios.get(`${API}/documents/categories`),
        ]);
        setDocuments(docsRes.data.documents || []);
        setDocCategories(catsRes.data.categories || []);
      } catch (err) {
        console.error('Failed to load documents:', err);
      }
    };
    fetchDocs();
  }, [fetchSettings]);

  // Sauvegarder l'annonce
  const handleSaveAnnouncement = async () => {
    setSavingAnnouncement(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/announcement`, {
        announcement: announcement.text,
        announcement_active: announcement.active,
        announcement_type: announcement.type
      });
      toast.success(announcement.active ? 'Annonce publiée' : 'Annonce sauvegardée');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSavingAnnouncement(false);
    }
  };

  // Sauvegarder les paramètres de réservation
  const handleSaveReservations = async () => {
    setSavingReservations(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/reservations/settings`, null, {
        params: {
          reservations_enabled: reservationsEnabled,
          reservations_max_hours: reservationsMaxDays,
          reservations_cancellation_policy: reservationsCancellationPolicy
        }
      });
      toast.success('Paramètres de réservation sauvegardés');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSavingReservations(false);
    }
  };

  // Sauvegarder les paramètres d'acompte
  const handleSaveDeposit = async () => {
    // Validation
    if (depositDefaultType === 'percentage' && (depositDefaultValue < 20 || depositDefaultValue > 50)) {
      toast.error('Le pourcentage d\'acompte doit être entre 20% et 50%');
      return;
    }
    if (depositDefaultType === 'fixed' && depositDefaultValue <= 0) {
      toast.error('Le montant d\'acompte doit être supérieur à 0€');
      return;
    }
    
    setSavingDeposit(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/deposit/settings`, null, {
        params: {
          deposit_enabled: depositEnabled,
          deposit_default_type: depositDefaultType,
          deposit_default_value: depositDefaultValue,
          deposit_apply_to_all: depositApplyToAll,
          deposit_min_order_amount: depositMinOrderAmount || null,
          deposit_refund_policy: depositRefundPolicy,
          deposit_refund_hours: depositRefundHours
        }
      });
      toast.success('Paramètres d\'acompte sauvegardés');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSavingDeposit(false);
    }
  };

  // Ajouter une date bloquée
  const handleAddBlockedDate = async () => {
    if (!newBlockedDate) return;
    
    try {
      await axios.post(`${API}/restaurant-dashboard/reservations/blocked-dates`, null, {
        params: { date: newBlockedDate }
      });
      setReservationsBlockedDates([...reservationsBlockedDates, newBlockedDate].sort());
      setNewBlockedDate('');
      toast.success('Date bloquée ajoutée');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  // Supprimer une date bloquée
  const handleRemoveBlockedDate = async (date) => {
    try {
      await axios.delete(`${API}/restaurant-dashboard/reservations/blocked-dates/${date}`);
      setReservationsBlockedDates(reservationsBlockedDates.filter(d => d !== date));
      toast.success('Date débloquée');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  const handleSave = async () => {
    // Vérifier que les modes de paiement sont configurés
    if (!settings.accepted_payment_methods || settings.accepted_payment_methods.length === 0) {
      toast.error('Veuillez sélectionner au moins un mode de paiement accepté au comptoir');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/settings`, {
        name: settings.name,
        address: settings.address,
        city: settings.city,
        zipcode: settings.zipcode,
        phone: settings.phone,
        email: settings.email,
        description: settings.description,
        cuisine_type: settings.cuisine_type,
        logo: settings.logo || null,
        cover_image: settings.cover_image || null,
        avg_prep_time: parseInt(settings.avg_prep_time),
        is_active: settings.is_active,
        accepted_payment_methods: settings.accepted_payment_methods,
        show_stock_alerts: showStockAlerts,
        iban: settings.iban || null,
        bic: settings.bic || null,
        vat_number: settings.vat_number || null
      });
      toast.success('Paramètres sauvegardés');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCuisineToggle = (cuisine) => {
    setSettings(prev => ({
      ...prev,
      cuisine_type: prev.cuisine_type.includes(cuisine)
        ? prev.cuisine_type.filter(c => c !== cuisine)
        : [...prev.cuisine_type, cuisine].slice(0, 3)
    }));
  };

  const handlePaymentMethodToggle = (methodId) => {
    setSettings(prev => ({
      ...prev,
      accepted_payment_methods: prev.accepted_payment_methods.includes(methodId)
        ? prev.accepted_payment_methods.filter(m => m !== methodId)
        : [...prev.accepted_payment_methods, methodId]
    }));
  };

  const handleAddressSelect = (addressData) => {
    setSettings(prev => ({
      ...prev,
      address: addressData.address || addressData.formatted_address,
      city: addressData.city || prev.city,
      zipcode: addressData.zipcode || prev.zipcode,
      latitude: addressData.latitude,
      longitude: addressData.longitude
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, WebP ou GIF.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop grande. Maximum 5 Mo.');
      return;
    }

    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingCover;
    const inputRef = type === 'logo' ? logoInputRef : coverInputRef;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings(prev => ({
        ...prev,
        [type === 'logo' ? 'logo' : 'cover_image']: response.data.url
      }));
      toast.success('Image uploadée !');
    } catch {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  // Remove image
  const handleRemoveImage = (type) => {
    setSettings(prev => ({
      ...prev,
      [type === 'logo' ? 'logo' : 'cover_image']: ''
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Paramètres du restaurant">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-2xl" />
          <div className="h-64 bg-gray-200 rounded-2xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Paramètres du restaurant">
      <div className="max-w-3xl space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-500" />
            Informations générales
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Nom du restaurant *</Label>
              <Input
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                className="mt-1.5 rounded-xl"
                data-testid="settings-name"
              />
            </div>

            <div className="sm:col-span-2">
              <Label>Adresse * (avec autocomplétion)</Label>
              <div className="mt-1.5">
                <AddressAutocomplete
                  value={settings.address}
                  onChange={(value) => setSettings({ ...settings, address: value })}
                  onSelect={handleAddressSelect}
                  placeholder="Tapez votre adresse..."
                />
              </div>
              {settings.latitude && settings.longitude && (
                <p className="text-xs text-emerald-600 mt-1">
                  Coordonnées GPS: {settings.latitude.toFixed(4)}, {settings.longitude.toFixed(4)}
                </p>
              )}
            </div>

            <div>
              <Label>Ville *</Label>
              <Input
                value={settings.city}
                onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                className="mt-1.5 rounded-xl"
              />
            </div>

            <div>
              <Label>Code postal *</Label>
              <Input
                value={settings.zipcode}
                onChange={(e) => setSettings({ ...settings, zipcode: e.target.value })}
                className="mt-1.5 rounded-xl"
              />
            </div>

            <div>
              <Label>Téléphone</Label>
              <Input
                value={settings.phone || ''}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                className="mt-1.5 rounded-xl"
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={settings.email || ''}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="mt-1.5 rounded-xl"
              />
            </div>

            <div className="sm:col-span-2 border-t border-gray-100 pt-4 mt-2">
              <h3 className="text-sm font-semibold text-gray-900" data-testid="billing-info-title">Coordonnées bancaires (facturation)</h3>
              <p className="text-xs text-gray-400 mt-0.5">Ces informations pré-remplissent automatiquement vos factures et reçus.</p>
            </div>

            <div className="sm:col-span-2">
              <Label>IBAN</Label>
              <Input
                value={settings.iban || ''}
                onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                placeholder="FR76 3000 6000 0112 3456 7890 189"
                className="mt-1.5 rounded-xl"
                data-testid="settings-iban-input"
              />
            </div>

            <div>
              <Label>BIC / SWIFT</Label>
              <Input
                value={settings.bic || ''}
                onChange={(e) => setSettings({ ...settings, bic: e.target.value })}
                placeholder="AGRIFRPP"
                className="mt-1.5 rounded-xl"
                data-testid="settings-bic-input"
              />
            </div>

            <div>
              <Label>N° TVA intracommunautaire</Label>
              <Input
                value={settings.vat_number || ''}
                onChange={(e) => setSettings({ ...settings, vat_number: e.target.value })}
                placeholder="FR12345678901"
                className="mt-1.5 rounded-xl"
                data-testid="settings-vat-input"
              />
            </div>

            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Textarea
                value={settings.description || ''}
                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                placeholder="Décrivez votre restaurant..."
                className="mt-1.5"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Cuisine Type */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Type de cuisine</h2>
          <p className="text-sm text-gray-500 mb-4">Sélectionnez jusqu&apos;à 3 types</p>
          
          <div className="flex flex-wrap gap-2">
            {cuisineOptions.map((cuisine) => (
              <button
                key={cuisine}
                onClick={() => handleCuisineToggle(cuisine)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  settings.cuisine_type.includes(cuisine)
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cuisine}
              </button>
            ))}
          </div>
        </div>

        {/* Images */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-emerald-500" />
            Images
          </h2>
          
          <div className="space-y-6">
            {/* Logo */}
            <div>
              <Label className="text-base font-medium">Logo du restaurant (200x200)</Label>
              <p className="text-sm text-gray-500 mb-3">Formats acceptés : JPG, PNG, WebP, GIF (max 5 Mo)</p>
              
              {settings.logo ? (
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <img src={getFullImageUrl(settings.logo)} alt="Logo" className="w-24 h-24 rounded-xl object-cover border border-gray-200" />
                    <button
                      onClick={() => handleRemoveImage('logo')}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1 text-sm text-gray-500 break-all">
                    {settings.logo}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => handleImageUpload(e, 'logo')}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="rounded-xl"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingLogo ? 'Upload...' : 'Uploader une image'}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                    <Input
                      value={settings.logo || ''}
                      onChange={(e) => setSettings({ ...settings, logo: e.target.value })}
                      placeholder="ou coller une URL..."
                      className="rounded-xl flex-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Cover Image */}
            <div>
              <Label className="text-base font-medium">Photo de couverture (1200x400)</Label>
              <p className="text-sm text-gray-500 mb-3">Cette image sera affichée en haut de la page de votre restaurant</p>
              
              {settings.cover_image ? (
                <div className="space-y-2">
                  <div className="relative">
                    <img src={getFullImageUrl(settings.cover_image)} alt="Cover" className="w-full h-40 rounded-xl object-cover border border-gray-200" />
                    <button
                      onClick={() => handleRemoveImage('cover')}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 break-all">{settings.cover_image}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => handleImageUpload(e, 'cover')}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      className="rounded-xl"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingCover ? 'Upload...' : 'Uploader une image'}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                    <Input
                      value={settings.cover_image || ''}
                      onChange={(e) => setSettings({ ...settings, cover_image: e.target.value })}
                      placeholder="ou coller une URL..."
                      className="rounded-xl flex-1"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Announcement / Actualités */}
        <div className={`bg-white rounded-2xl border p-6 ${
          announcement.active 
            ? announcement.type === 'promo' ? 'border-emerald-400 bg-emerald-50/30'
            : announcement.type === 'alert' ? 'border-red-400 bg-red-50/30'
            : 'border-blue-400 bg-blue-50/30'
            : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-emerald-500" />
              Message d'actualité
            </h2>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${announcement.active ? 'text-emerald-600 font-medium' : 'text-gray-500'}`}>
                {announcement.active ? 'Actif' : 'Inactif'}
              </span>
              <Switch
                checked={announcement.active}
                onCheckedChange={(checked) => setAnnouncement({ ...announcement, active: checked })}
                data-testid="announcement-toggle"
              />
            </div>
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            Ce message sera affiché en haut de votre page restaurant pour informer vos clients 
            (promotions, événements, nouveautés, fermeture exceptionnelle...).
          </p>
          
          {/* Type d'annonce */}
          <div className="mb-4">
            <Label className="mb-2 block">Type d'annonce</Label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setAnnouncement({ ...announcement, type: 'promo' })}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  announcement.type === 'promo'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="announcement-type-promo"
              >
                <div className={`w-3 h-3 rounded-full mb-2 ${announcement.type === 'promo' ? 'bg-emerald-500' : 'bg-emerald-300'}`} />
                <p className={`font-medium text-sm ${announcement.type === 'promo' ? 'text-emerald-700' : 'text-gray-700'}`}>
                  Promo
                </p>
                <p className="text-xs text-gray-500">Offres, réductions</p>
              </button>
              <button
                type="button"
                onClick={() => setAnnouncement({ ...announcement, type: 'info' })}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  announcement.type === 'info'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="announcement-type-info"
              >
                <div className={`w-3 h-3 rounded-full mb-2 ${announcement.type === 'info' ? 'bg-blue-500' : 'bg-blue-300'}`} />
                <p className={`font-medium text-sm ${announcement.type === 'info' ? 'text-blue-700' : 'text-gray-700'}`}>
                  Info
                </p>
                <p className="text-xs text-gray-500">Nouveautés, événements</p>
              </button>
              <button
                type="button"
                onClick={() => setAnnouncement({ ...announcement, type: 'alert' })}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  announcement.type === 'alert'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="announcement-type-alert"
              >
                <div className={`w-3 h-3 rounded-full mb-2 ${announcement.type === 'alert' ? 'bg-red-500' : 'bg-red-300'}`} />
                <p className={`font-medium text-sm ${announcement.type === 'alert' ? 'text-red-700' : 'text-gray-700'}`}>
                  Alerte
                </p>
                <p className="text-xs text-gray-500">Fermeture, urgent</p>
              </button>
            </div>
          </div>
          
          <Textarea
            value={announcement.text}
            onChange={(e) => setAnnouncement({ ...announcement, text: e.target.value })}
            placeholder={
              announcement.type === 'promo' 
                ? "Ex: 🎉 -20% sur tous les burgers cette semaine !"
                : announcement.type === 'alert'
                ? "Ex: ⚠️ Fermeture exceptionnelle le 25 décembre"
                : "Ex: 🆕 Découvrez notre nouvelle carte de printemps !"
            }
            className="rounded-xl mb-3 min-h-[100px]"
            maxLength={500}
            data-testid="announcement-text"
          />
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {announcement.text.length}/500 caractères
            </span>
            <Button
              onClick={handleSaveAnnouncement}
              disabled={savingAnnouncement}
              size="sm"
              className={
                announcement.active 
                  ? announcement.type === 'promo' ? 'bg-emerald-500 hover:bg-emerald-600'
                  : announcement.type === 'alert' ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-blue-500 hover:bg-blue-600'
                  : ''
              }
              data-testid="save-announcement"
            >
              {savingAnnouncement ? 'Sauvegarde...' : announcement.active ? 'Publier l\'annonce' : 'Sauvegarder'}
            </Button>
          </div>
          
          {announcement.active && announcement.text && (
            <div className={`mt-4 p-4 rounded-xl text-white ${
              announcement.type === 'promo' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
              : announcement.type === 'alert' ? 'bg-gradient-to-r from-red-500 to-red-600'
              : 'bg-gradient-to-r from-blue-500 to-blue-600'
            }`} data-testid="announcement-preview">
              <p className="text-xs font-medium mb-1 opacity-80">Aperçu de votre annonce :</p>
              <p className="text-sm">{announcement.text}</p>
            </div>
          )}
        </div>

        {/* Preparation */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            Préparation
          </h2>
          
          <div>
            <Label>Temps moyen de préparation (minutes)</Label>
            <Input
              type="number"
              value={settings.avg_prep_time}
              onChange={(e) => setSettings({ ...settings, avg_prep_time: e.target.value })}
              className="mt-1.5 rounded-xl w-32"
              min={5}
              max={120}
            />
          </div>
        </div>

        {/* Payment Methods at Counter - OBLIGATOIRE */}
        <div className={`bg-white rounded-2xl border p-6 ${
          (!settings.accepted_payment_methods || settings.accepted_payment_methods.length === 0)
            ? 'border-amber-400 bg-amber-50'
            : 'border-gray-200'
        }`}>
          <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-500" />
            Modes de paiement acceptés au comptoir
            <span className="text-red-500">*</span>
          </h2>
          
          {(!settings.accepted_payment_methods || settings.accepted_payment_methods.length === 0) && (
            <div className="flex items-center gap-2 text-amber-700 bg-amber-100 rounded-lg p-3 mb-4">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <p className="text-sm">
                <strong>Configuration obligatoire :</strong> Vous devez sélectionner au moins un mode de paiement 
                pour que vos clients puissent commander.
              </p>
            </div>
          )}
          
          <p className="text-sm text-gray-500 mb-4">
            Indiquez les modes de paiement que vous acceptez lorsque les clients viennent récupérer leur commande.
          </p>
          
          <div className="grid sm:grid-cols-2 gap-3">
            {paymentMethodOptions.map((method) => {
              const isSelected = settings.accepted_payment_methods?.includes(method.id);
              return (
                <div
                  key={method.id}
                  onClick={() => handlePaymentMethodToggle(method.id)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  data-testid={`payment-method-${method.id}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handlePaymentMethodToggle(method.id)}
                    className="pointer-events-none"
                  />
                  <span className="text-xl">{method.icon}</span>
                  <span className={`font-medium ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>
                    {method.label}
                  </span>
                </div>
              );
            })}
          </div>
          
          {settings.accepted_payment_methods?.length > 0 && (
            <p className="text-sm text-emerald-600 mt-4">
              ✓ {settings.accepted_payment_methods.length} mode(s) de paiement sélectionné(s)
            </p>
          )}
        </div>

        {/* Push Notifications */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-500" />
            Notifications push
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Recevez des notifications dans votre navigateur pour les nouvelles commandes, même si l'onglet est fermé.
          </p>
          <PushNotificationToggle />
          
          {/* Stock Alerts Toggle */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Alertes de stock</p>
                <p className="text-sm text-gray-500">
                  Afficher les alertes de stock bas sur le tableau de bord
                </p>
              </div>
              <Switch
                checked={showStockAlerts}
                onCheckedChange={setShowStockAlerts}
                data-testid="toggle-stock-alerts"
              />
            </div>
          </div>
        </div>

        {/* Réservations à l'avance */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="reservations-settings">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-500" />
            Réservations à l'avance
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Permettez à vos clients de réserver leur commande pour un jour futur (événements, fêtes, traiteur...).
          </p>
          
          <div className="space-y-6">
            {/* Toggle activation */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">Accepter les réservations</p>
                <p className="text-sm text-gray-500">
                  Les clients pourront commander pour des jours futurs
                </p>
              </div>
              <Switch
                checked={reservationsEnabled}
                onCheckedChange={setReservationsEnabled}
                data-testid="reservations-toggle"
              />
            </div>

            {reservationsEnabled && (
              <>
                {/* Délai maximum */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Réservations possibles jusqu'à
                  </Label>
                  <select
                    value={reservationsMaxDays}
                    onChange={(e) => setReservationsMaxDays(parseInt(e.target.value))}
                    className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="reservations-max-days"
                  >
                    {maxDaysOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} à l'avance
                      </option>
                    ))}
                  </select>
                </div>

                {/* Politique d'annulation */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Politique d'annulation
                  </Label>
                  <Textarea
                    value={reservationsCancellationPolicy}
                    onChange={(e) => setReservationsCancellationPolicy(e.target.value)}
                    placeholder="Ex: Annulation possible jusqu'à 24h avant le retrait. Au-delà, aucun remboursement ne sera effectué."
                    className="min-h-[100px] rounded-xl"
                    data-testid="reservations-cancellation-policy"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Ce texte sera affiché aux clients lors de leur réservation
                  </p>
                </div>

                {/* Dates bloquées */}
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <CalendarX className="w-4 h-4" />
                    Dates bloquées
                  </Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Bloquez certaines dates où vous ne souhaitez pas recevoir de réservations
                  </p>
                  
                  <div className="flex gap-2 mb-3">
                    <Input
                      type="date"
                      value={newBlockedDate}
                      onChange={(e) => setNewBlockedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="flex-1 h-12 rounded-xl"
                      data-testid="new-blocked-date"
                    />
                    <Button
                      onClick={handleAddBlockedDate}
                      disabled={!newBlockedDate}
                      variant="outline"
                      className="rounded-xl"
                      data-testid="add-blocked-date"
                    >
                      Bloquer
                    </Button>
                  </div>
                  
                  {reservationsBlockedDates.length > 0 && (
                    <div className="space-y-2">
                      {reservationsBlockedDates.sort().map(date => (
                        <div 
                          key={date}
                          className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg"
                        >
                          <span className="text-sm text-red-700">
                            {new Date(date).toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                          <button
                            onClick={() => handleRemoveBlockedDate(date)}
                            className="p-1 hover:bg-red-100 rounded text-red-500"
                            title="Débloquer cette date"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bouton sauvegarder */}
                <Button
                  onClick={handleSaveReservations}
                  disabled={savingReservations}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                  data-testid="save-reservations"
                >
                  {savingReservations ? 'Sauvegarde...' : 'Sauvegarder les paramètres de réservation'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Deposit (Acompte) Settings */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="deposit-settings-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-amber-100 rounded-xl">
              <Wallet className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Acomptes</h2>
              <p className="text-sm text-gray-500">Demandez un acompte sur certaines commandes</p>
            </div>
          </div>

          <div className="space-y-5">
            {/* Toggle activation */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-900">Activer les acomptes</p>
                <p className="text-sm text-gray-500">Les clients paieront un acompte en ligne, le reste sur place</p>
              </div>
              <Switch
                checked={depositEnabled}
                onCheckedChange={setDepositEnabled}
                data-testid="deposit-toggle"
              />
            </div>

            {depositEnabled && (
              <>
                {/* Type d'acompte */}
                <div className="space-y-3">
                  <Label>Type d'acompte par défaut</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setDepositDefaultType('percentage');
                        setDepositDefaultValue(30); // Reset to default %
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        depositDefaultType === 'percentage'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="deposit-type-percentage"
                    >
                      <Percent className={`w-5 h-5 mb-2 ${depositDefaultType === 'percentage' ? 'text-amber-600' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${depositDefaultType === 'percentage' ? 'text-amber-700' : 'text-gray-900'}`}>
                        Pourcentage
                      </p>
                      <p className="text-sm text-gray-500">Ex: 30% du total</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDepositDefaultType('fixed');
                        setDepositDefaultValue(10); // Reset to default €
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        depositDefaultType === 'fixed'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="deposit-type-fixed"
                    >
                      <Euro className={`w-5 h-5 mb-2 ${depositDefaultType === 'fixed' ? 'text-amber-600' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${depositDefaultType === 'fixed' ? 'text-amber-700' : 'text-gray-900'}`}>
                        Montant fixe
                      </p>
                      <p className="text-sm text-gray-500">Ex: 10€ par article</p>
                    </button>
                  </div>
                </div>

                {/* Valeur de l'acompte */}
                <div className="space-y-2">
                  <Label>
                    {depositDefaultType === 'percentage' ? 'Pourcentage d\'acompte (20-50%)' : 'Montant d\'acompte (€)'}
                  </Label>
                  {depositDefaultType === 'percentage' ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="20"
                          max="50"
                          step="5"
                          value={depositDefaultValue}
                          onChange={(e) => setDepositDefaultValue(Number(e.target.value))}
                          className="flex-1 accent-amber-500"
                          data-testid="deposit-value-slider"
                        />
                        <span className="text-2xl font-bold text-amber-600 min-w-[60px] text-right">
                          {depositDefaultValue}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>20%</span>
                        <span>35%</span>
                        <span>50%</span>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <Euro className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="number"
                        min="1"
                        step="0.5"
                        value={depositDefaultValue}
                        onChange={(e) => setDepositDefaultValue(Number(e.target.value))}
                        className="pl-12 h-12 rounded-xl"
                        placeholder="10"
                        data-testid="deposit-value-input"
                      />
                    </div>
                  )}
                </div>

                {/* Application aux produits */}
                <div className="space-y-3">
                  <Label>Application aux produits</Label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="radio"
                        name="deposit_apply"
                        checked={!depositApplyToAll}
                        onChange={() => setDepositApplyToAll(false)}
                        className="accent-amber-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Configurer par produit</p>
                        <p className="text-sm text-gray-500">Choisissez les plats qui nécessitent un acompte dans le menu</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="radio"
                        name="deposit_apply"
                        checked={depositApplyToAll}
                        onChange={() => setDepositApplyToAll(true)}
                        className="accent-amber-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Appliquer à tous les produits</p>
                        <p className="text-sm text-gray-500">Un acompte sera demandé sur chaque commande</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Montant minimum */}
                <div className="space-y-2">
                  <Label>Montant minimum de commande pour l'acompte (optionnel)</Label>
                  <div className="relative">
                    <Euro className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="number"
                      min="0"
                      step="5"
                      value={depositMinOrderAmount}
                      onChange={(e) => setDepositMinOrderAmount(e.target.value)}
                      className="pl-12 h-12 rounded-xl"
                      placeholder="Ex: 50 (laissez vide pour aucun minimum)"
                      data-testid="deposit-min-amount"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    L'acompte ne sera demandé que si le total de la commande dépasse ce montant
                  </p>
                </div>

                {/* Politique de remboursement */}
                <div className="space-y-2">
                  <Label>Politique de remboursement</Label>
                  <Textarea
                    value={depositRefundPolicy}
                    onChange={(e) => setDepositRefundPolicy(e.target.value)}
                    placeholder="Ex: Acompte remboursable intégralement si annulation plus de 48h avant le retrait. Non remboursable en cas d'annulation tardive."
                    className="min-h-[100px] rounded-xl"
                    data-testid="deposit-refund-policy"
                  />
                </div>

                {/* Délai d'annulation */}
                <div className="space-y-2">
                  <Label>Délai d'annulation pour remboursement intégral</Label>
                  <select
                    value={depositRefundHours}
                    onChange={(e) => setDepositRefundHours(Number(e.target.value))}
                    className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    data-testid="deposit-refund-hours"
                  >
                    <option value={0}>Pas de remboursement</option>
                    <option value={24}>24 heures avant</option>
                    <option value={48}>48 heures avant</option>
                    <option value={72}>72 heures avant</option>
                    <option value={168}>1 semaine avant</option>
                  </select>
                </div>

                {/* Info box */}
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Comment ça fonctionne ?</p>
                    <ul className="space-y-1 text-blue-600">
                      <li>• Le client paie l'acompte en ligne lors de la commande</li>
                      <li>• Le reste est payé sur place au moment du retrait</li>
                      <li>• Vous pouvez configurer l'acompte par plat dans la section Menu</li>
                    </ul>
                  </div>
                </div>

                {/* Bouton sauvegarder */}
                <Button
                  onClick={handleSaveDeposit}
                  disabled={savingDeposit}
                  className="w-full bg-amber-500 hover:bg-amber-600 rounded-xl"
                  data-testid="save-deposit"
                >
                  {savingDeposit ? 'Sauvegarde...' : 'Sauvegarder les paramètres d\'acompte'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Status */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">Statut du restaurant</h2>
              <p className="text-sm text-gray-500">
                {settings.is_active ? 'Visible sur la plateforme' : 'Masqué de la plateforme'}
              </p>
            </div>
            <Switch
              checked={settings.is_active}
              onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
            />
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="documents-section">
          <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            Documents obligatoires
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Envoyez vos documents pour validation par l'equipe LEMO (PDF uniquement)
          </p>

          {/* Upload */}
          <div className="flex items-center gap-3 mb-4">
            <select
              id="doc-category"
              className="h-10 rounded-lg border border-gray-200 px-3 text-sm"
              data-testid="doc-category-select"
            >
              {docCategories.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 px-4 h-10 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg cursor-pointer text-sm font-medium">
              {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploadingDoc ? 'Envoi...' : 'Envoyer un PDF'}
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                data-testid="doc-file-input"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setUploadingDoc(true);
                  try {
                    const cat = document.getElementById('doc-category').value;
                    const formData = new FormData();
                    formData.append('file', file);
                    const res = await axios.post(`${API}/documents/upload?category=${cat}`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    setDocuments(prev => [res.data.document, ...prev]);
                    toast.success('Document envoye avec succes');
                  } catch {
                    toast.error("Erreur lors de l'envoi");
                  } finally {
                    setUploadingDoc(false);
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>

          {/* Documents list */}
          {documents.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Aucun document envoye</p>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50" data-testid={`doc-${doc.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="text-sm font-medium">{docCategories.find(c => c.id === doc.category)?.label || doc.category}</p>
                      <p className="text-xs text-gray-400">{doc.original_name} - {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.status === 'approved' && <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium"><CheckCircle className="w-3.5 h-3.5" /> Approuve</span>}
                    {doc.status === 'rejected' && <span className="flex items-center gap-1 text-xs text-red-500 font-medium"><XCircle className="w-3.5 h-3.5" /> Refuse</span>}
                    {doc.status === 'pending' && <span className="text-xs text-yellow-600 font-medium">En attente</span>}
                    <a href={`${BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-500 hover:underline ml-2">Voir</a>
                    <button
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/documents/${doc.id}`);
                          setDocuments(prev => prev.filter(d => d.id !== doc.id));
                          toast.success('Document supprime');
                        } catch { toast.error('Erreur'); }
                      }}
                      className="text-gray-400 hover:text-red-500 ml-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 rounded-full px-8"
          data-testid="save-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
        </Button>
      </div>
    </DashboardLayout>
  );
}
